import { ReactElement, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { BottomDrawerModel, MultiDateRangePicker, Dropdown } from '@components/common';
import { useFormik } from 'formik';
import { useOutsideDialogClose } from '@hooks/useOutsideDialogClose';
import { useSiteListFilterStore, SiteListFilterObject } from '@store/lms/siteListFilterStore';
import { useCompetitionFilterStore } from '@store/lms/competitionFilterStore';
import { siteTypeList } from '@components/lms/addNewSite/components/siteInformation';
import { contractorTypeFilterList } from '@components/lms/siteListWrapper/components/siteListFilters/index';
import RadioButtonChip from '@components/common/formControl/radioButtonChip/radioButtonChip';
import { useSSDropdownQuery } from '@services/hooks/lms/addNewSite/useSSDropdownQuery';
import { useSSDropdownRxDb } from '@services/hooks/lms/addNewSite/useSSDropdownRxDb';
import GoogleSearchInput from '@components/googleSearch/googleSearch';
import { IMapSearchInput } from '@components/googleSearch';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { RoleType } from '@helper/utils';

export interface ISitesFiltersProps {
	openFilters: boolean;
	closeFilters: () => void;
}

export const SiteListFilters = (props: ISitesFiltersProps): ReactElement => {
	const { openFilters, closeFilters } = props;
	const location = useLocation();
	const userRoleType = useUserRoleType();
	const dialogRef = useRef<HTMLDivElement | null>(null);
	useOutsideDialogClose({ dialogRef, callBackFunction: closeFilters });

	// Check if current route is competition
	const isCompetitionRoute = location.pathname === '/lms/sites/competition';
	// Check if current route is all sites
	const isAllSitesRoute = location.pathname === '/lms/sites';
	// Check if current route is upcoming
	const isUpcomingRoute = location.pathname === '/lms/sites/upcoming';
	// Check if user is SS (Site Specialist)
	const isSSUser = userRoleType === RoleType.SS;

	// Hide source filter when:
	// SS user is on all sites route or upcoming route
	const shouldHideSourceFilter = isSSUser && (isAllSitesRoute || isUpcomingRoute);

	// Hide specific filters for upcoming route
	const shouldHideUpcomingFilters = isUpcomingRoute;

	// Get the appropriate filter store based on route
	const {
		siteListFilter: allSiteFilter,
		updateFilter: updateAllSiteFilter,
		resetFilter: resetAllSiteFilter,
	} = useSiteListFilterStore();
	const {
		competitionFilter,
		upcomingFilter,
		updateFilter: updateCompetitionFilter,
		resetFilter: resetCompetitionFilter,
	} = useCompetitionFilterStore();

	// Determine which filter to use based on route
	const { filterData, filterKey, updateFilter, resetFilter } = useMemo(() => {
		if (isCompetitionRoute) {
			return {
				filterData: competitionFilter,
				filterKey: 'competitionFilter',
				// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
				updateFilter: (filterKey: string, values: SiteListFilterObject) => updateCompetitionFilter(filterKey, values),
				resetFilter: resetCompetitionFilter,
			};
		}
		if (isUpcomingRoute) {
			return {
				filterData: upcomingFilter,
				filterKey: 'upcomingFilter',
				// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
				updateFilter: (filterKey: string, values: SiteListFilterObject) => updateCompetitionFilter(filterKey, values),
				resetFilter: resetCompetitionFilter,
			};
		}
		// All Sites route
		return {
			filterData: allSiteFilter,
			filterKey: 'siteListFilter',
			// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
			updateFilter: (_filterKey: string, values: SiteListFilterObject) => updateAllSiteFilter(values),
			resetFilter: resetAllSiteFilter,
		};
	}, [
		isCompetitionRoute,
		isUpcomingRoute,
		competitionFilter,
		upcomingFilter,
		allSiteFilter,
		updateCompetitionFilter,
		updateAllSiteFilter,
		resetCompetitionFilter,
		resetAllSiteFilter,
	]);

	// Ensure SS data is loaded and read from RxDB
	useSSDropdownQuery({ enabled: openFilters && !shouldHideSourceFilter }); // Load data if needed
	const { data: ssList } = useSSDropdownRxDb(); // Read from RxDB

	const ssListOptions =
		ssList?.map((ss) => ({
			id: ss.mdicode_c,
			value: ss.name || ss.mdicode_c,
			name: ss.name || ss.mdicode_c,
			actualValue: ss.mdicode_c,
		})) || [];

	const getDisplayName = (selectedValue: string): string => {
		const selectedOption = ssListOptions.find((option) => option.id === selectedValue);
		return selectedOption?.name || selectedValue;
	};

	const formik = useFormik<SiteListFilterObject>({
		initialValues: filterData,
		onSubmit: (values) => {
			updateFilter(filterKey, values);
			closeFilters();
		},
	});

	// Update formik values when filterData changes (e.g., when switching routes)
	useEffect(() => {
		formik.setValues(filterData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterData]);

	const handleSourceChange = (option: { id: string | number; value: string; name?: string }): void => {
		formik.setFieldValue('source', option.id.toString());
	};

	const handleLocationSelection = useCallback(
		(venueData: IMapSearchInput): void => {
			formik.setFieldValue('location', {
				lat: venueData.lat,
				lng: venueData.lng,
				name: venueData.name || venueData.formatted_address || '',
			});
		},
		[formik.setFieldValue]
	);

	const handleLocationSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value;
		formik.setFieldValue('location.name', value);

		if (!value.trim() && !formik.values.location?.lat && !formik.values.location?.lng) {
			formik.setFieldValue('location', { lat: null, lng: null, name: '' });
		}
	};

	const handleLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (e.key === 'Enter') {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleSubmit = (e?: React.MouseEvent<HTMLButtonElement>): void => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		formik.handleSubmit();
	};

	const clearAll = (): void => {
		resetFilter(filterKey);
		closeFilters();
	};

	const isFilterChecked =
		formik.values.createdAt.every((item) => item?.toString().length) ||
		(!shouldHideUpcomingFilters && formik.values.lastActivityDate.every((item) => item?.toString().length)) ||
		formik.values.siteType ||
		(!shouldHideUpcomingFilters && formik.values.contractorType) ||
		(!shouldHideUpcomingFilters && formik.values.convertStatus) ||
		(!shouldHideSourceFilter && formik.values.source) ||
		formik.values.location.name.length > 0 ||
		(isUpcomingRoute && formik.values.date_of_next_visit.every((item) => item?.toString().length));

	const handleCheckboxChangeEvent = (e: React.ChangeEvent<HTMLInputElement>): void => {
		if (!e || !e.target) {
			return;
		}

		const name = e.target.name;
		const value = e?.target?.checked;
		formik.setFieldValue(name, value);
	};

	useEffect(() => {
		const overlay = document.getElementById('overlay') as HTMLElement;
		document.body.classList.add('overflow-hidden');
		overlay.classList.add('show');
	}, []);

	return (
		<BottomDrawerModel openModelStatus={openFilters} modelId="filter-main-wrapper">
			<div className="container">
				<button
					type="button"
					aria-label="filter popup"
					className="filter-sort-icon icon-filter"
					onClick={closeFilters}
				></button>
				<div ref={dialogRef} className="filter-drawer-inner-wrapper filter">
					<span className="filter-title">
						<p className="h2">Filter</p>
						<button
							className="clear-all"
							type="button"
							hidden={!isFilterChecked}
							onClick={clearAll}
							aria-label="Clear All"
						>
							Clear All
						</button>
					</span>
					<form onSubmit={handleFormSubmit}>
						<div className="form-fieldset row">
							<div className="d-col">
								<span className="form-title h4">
									<span className="icon-date"></span>Date of Created
								</span>
								<MultiDateRangePicker
									id="created"
									name="createdAt"
									value={formik.values.createdAt}
									formik={formik}
									maxDate={new Date()}
								/>
							</div>
							{/* Hide Date of Visit filter for upcoming route */}
							{!shouldHideUpcomingFilters && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-date"></span>Date of Visit
									</span>
									<MultiDateRangePicker
										id="lastActivityDate"
										name="lastActivityDate"
										value={formik.values.lastActivityDate}
										formik={formik}
										maxDate={new Date()}
									/>
								</div>
							)}
							{isUpcomingRoute && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-date"></span>Tentative Woodworking Start Date
									</span>
									<MultiDateRangePicker
										id="date_of_next_visit"
										name="date_of_next_visit"
										value={formik.values.date_of_next_visit}
										formik={formik}
										// maxDate={new Date()}
									/>
								</div>
							)}
							<div className="d-col">
								<span className="form-title h4">
									<span className="icon-location"></span>Location
								</span>
								<div className="location-search-box">
									<GoogleSearchInput
										id="location-search"
										name="location.name"
										type="text"
										value={formik.values.location?.name || ''}
										onChange={handleLocationSearchChange}
										onKeyDown={handleLocationKeyDown}
										placeholder="Search for a location or address..."
										handleVenueSelection={handleLocationSelection}
										required={false}
										label=""
										setFieldValue={formik.setFieldValue}
									/>
								</div>
							</div>
							{/* Hide source filter for competition route or SS user on all sites route */}
							{!shouldHideSourceFilter && !isSSUser && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-user"></span>Source
									</span>
									<Dropdown
										name="source"
										id="source"
										onBlur={formik.handleBlur}
										title={getDisplayName(formik.values.source ?? '')}
										error={formik.touched.source && formik.errors.source ? formik.errors.source : null}
										options={ssListOptions}
										setFieldValue={formik.setFieldValue}
										onChange={handleSourceChange}
										placeholder="Select Site Specialist"
									/>
								</div>
							)}
							<div className="d-col">
								<RadioButtonChip
									label="Site Type"
									options={siteTypeList}
									value={formik.values.siteType ?? ''}
									onChange={(val) => {
										formik.setFieldValue('siteType', val);
									}}
									onBlur={() => formik.setFieldTouched('siteType', true)}
									error={formik.touched.siteType && formik.errors.siteType ? formik.errors.siteType : null}
									required
								/>
							</div>
							{!shouldHideUpcomingFilters && (
								<div className="d-col">
									<RadioButtonChip
										label="Contractor Type"
										options={contractorTypeFilterList}
										value={formik.values.contractorType ?? ''}
										onChange={(val) => formik.setFieldValue('contractorType', val)}
										onBlur={() => formik.setFieldTouched('contractorType', true)}
										error={
											formik.touched.contractorType && formik.errors.contractorType
												? formik.errors.contractorType
												: null
										}
										required
									/>
								</div>
							)}
							{!shouldHideUpcomingFilters && (
								<div className="d-col">
									<div className="sites-label">
										<span className="form-title h4">
											<span className="icon-user"> </span>Converted Sites
										</span>

										<ul className="custom-toggle">
											<li className="custom-toggle-item">
												<input
													type="checkbox"
													checked={formik.values.convertStatus}
													name="convertStatus"
													id="convertStatus"
													onChange={handleCheckboxChangeEvent}
												/>
												<label htmlFor="convertStatus">
													<span className="visually-hidden">convertStatus</span>
												</label>
											</li>
										</ul>
									</div>
								</div>
							)}
						</div>
					</form>
					<div className="d-col submit">
						<button
							type="button"
							disabled={!isFilterChecked}
							onClick={handleSubmit}
							className="btn btn-primary btn-medium"
						>
							Submit
						</button>
					</div>
				</div>
			</div>
		</BottomDrawerModel>
	);
};
